import * as THREE from 'three';
import { npcActive, type NpcDef } from '../content/npcs';
import { damp, dampAngle } from '../core/math';
import type { Random } from '../core/Random';
import { buildCharacter, CharacterAnimator, type CharacterRig, type Pose } from '../world/builders/character';
import type { CollisionWorld } from '../world/Collision';

type NpcState = 'idle' | 'walk' | 'talk' | 'flee';

/** Where a stage (darts, arm wrestling, cornhole...) wants an NPC, and how they should stand. */
export interface NpcHold {
  x: number;
  z: number;
  heading: number;
  pose: Pose;
  seat?: number;
  /** Fine-tune the rig after the pose is applied (an arm locked in a struggle). */
  after?: (rig: CharacterRig, time: number) => void;
}

const FLEE_LOOP = [
  new THREE.Vector3(1.5, 0, 36),
  new THREE.Vector3(1.5, 0, -40),
  new THREE.Vector3(30, 0, -40),
  new THREE.Vector3(30, 0, 36),
];

export class NpcAgent {
  readonly rig: CharacterRig;
  readonly anim: CharacterAnimator;
  readonly position = new THREE.Vector3();
  heading = 0;
  state: NpcState = 'idle';
  active = true;
  private readonly target = new THREE.Vector3();
  private timer = 0;
  private idlePose: Pose = 'stand';
  private stuck = 0;
  private fleeIndex = 0;
  speed = 0;
  /** While set, a stage owns this NPC: they stand (or sit) where they're told. */
  hold: NpcHold | null = null;

  constructor(
    readonly def: NpcDef,
    parent: THREE.Object3D,
    private readonly rng: Random,
  ) {
    this.rig = buildCharacter(def.look);
    this.anim = new CharacterAnimator(this.rig);
    this.position.set(def.home.x, 0, def.home.z);
    this.heading = def.facing ?? rng.range(0, Math.PI * 2);
    this.timer = rng.range(0, 3);
    parent.add(this.rig.root);
  }

  /** Stop and look at someone (the player) while talking. */
  faceToward(x: number, z: number): void {
    if (this.def.sitting) return;
    this.state = 'talk';
    this.heading = Math.atan2(x - this.position.x, z - this.position.z);
  }

  endTalk(): void {
    if (this.state === 'talk') {
      this.state = 'idle';
      this.timer = 2;
    }
  }

  flee(on: boolean): void {
    if (on) {
      this.state = 'flee';
      this.fleeIndex = 0;
    } else if (this.state === 'flee') {
      this.state = 'idle';
      this.position.set(this.def.home.x, 0, this.def.home.z);
    }
  }

  update(dt: number, time: number, minutes: number, collision: CollisionWorld): void {
    const h = this.hold;
    if (h) {
      this.active = true;
      this.rig.root.visible = true;
      this.position.set(h.x, 0, h.z);
      this.heading = h.heading;
      this.anim.update({ dt, time, speed: 0, pose: h.pose, seat: h.seat });
      h.after?.(this.rig, time);
      this.rig.root.position.copy(this.position);
      this.rig.root.rotation.y = this.heading;
      return;
    }
    const shouldBeActive = npcActive(this.def, minutes) || this.state === 'flee';
    if (shouldBeActive !== this.active) {
      this.active = shouldBeActive;
      if (shouldBeActive) this.position.set(this.def.home.x, 0, this.def.home.z);
    }
    this.rig.root.visible = this.active;
    if (!this.active) return;

    let pose: Pose = this.def.sitting ? 'rock' : 'stand';
    let moveSpeed = 0;

    if (this.def.sitting) {
      this.heading = this.def.facing ?? 0;
    } else if (this.state === 'talk') {
      pose = 'stand';
    } else if (this.state === 'flee') {
      const target = FLEE_LOOP[this.fleeIndex % FLEE_LOOP.length] as THREE.Vector3;
      moveSpeed = this.moveToward(target, 6.5, dt, collision);
      if (this.position.distanceTo(target) < 1.5) this.fleeIndex++;
    } else if (this.def.wander > 0) {
      this.timer -= dt;
      if (this.state === 'idle') {
        pose = this.idlePose;
        if (this.timer <= 0) {
          const a = this.rng.range(0, Math.PI * 2);
          const r = this.rng.range(0.5, this.def.wander);
          this.target.set(this.def.home.x + Math.cos(a) * r, 0, this.def.home.z + Math.sin(a) * r);
          this.state = 'walk';
          this.timer = 8;
          this.stuck = 0;
        }
      } else if (this.state === 'walk') {
        moveSpeed = this.moveToward(this.target, 1.35, dt, collision);
        if (this.position.distanceTo(this.target) < 0.4 || this.timer <= 0 || this.stuck > 1.2) {
          this.state = 'idle';
          this.timer = this.rng.range(2.5, 7);
          const drinker = this.def.id === 'earl' || this.def.id === 'dale';
          this.idlePose =
            drinker && this.rng.chance(0.4) ? 'drink' : this.rng.chance(0.12) ? 'wave' : 'stand';
        }
      }
    }

    this.speed = damp(this.speed, moveSpeed, 10, dt);
    this.anim.update({ dt, time, speed: this.speed, pose, sprinting: this.state === 'flee', seat: 0.48 });
    this.rig.root.position.copy(this.position);
    this.rig.root.rotation.y = this.heading;
  }

  private moveToward(target: THREE.Vector3, speed: number, dt: number, collision: CollisionWorld): number {
    const dx = target.x - this.position.x;
    const dz = target.z - this.position.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.05) return 0;
    const step = Math.min(dist, speed * dt);
    const next = { x: this.position.x + (dx / dist) * step, z: this.position.z + (dz / dist) * step };
    collision.resolve(next, 0.35);
    const moved = Math.hypot(next.x - this.position.x, next.z - this.position.z);
    if (moved < step * 0.3) this.stuck += dt;
    this.position.x = next.x;
    this.position.z = next.z;
    this.heading = dampAngle(this.heading, Math.atan2(dx, dz), 8, dt);
    return dt > 0 ? moved / dt : 0;
  }
}

export class NpcManager {
  readonly agents: NpcAgent[];

  constructor(defs: readonly NpcDef[], parent: THREE.Object3D, rng: Random) {
    this.agents = defs.map((d) => new NpcAgent(d, parent, rng));
  }

  get(id: string): NpcAgent | undefined {
    return this.agents.find((a) => a.def.id === id);
  }

  update(dt: number, time: number, minutes: number, collision: CollisionWorld): void {
    for (const a of this.agents) a.update(dt, time, minutes, collision);
  }
}
